/**
 * Furniture placement (Phase 1c, static). Places props per room type using the
 * archetype tiles (PropLow/PropTall/PropDecor) with a per-prop glyph override, so
 * a desk and a cabinet read distinctly while sharing engine behaviour. Prop
 * IDENTITY = glyph override on the tile (mirrors the janitor-cabinet pattern);
 * function/verb behaviour is layered on in later phases.
 *
 * Connectivity-safe: solid/cover props are placed only on a room's interior
 * PERIMETER ring, never on a tile orthogonally adjacent to a door (keeps door
 * lanes + the room centre open), so the floor stays fully traversable. Passable
 * decor (plants) may sit anywhere. Pure + deterministic.
 */

import { setTile, tileAt, type MapData } from '../map';
import { TileType } from '../tiles';
import { cellFloor, type Rect } from './bsp';
import type { RoomType } from './rooms';

interface PropSpec {
  tile: TileType;
  glyph: string;
}

const DESK: PropSpec = { tile: TileType.PropLow, glyph: 'π' };
const COUNTER: PropSpec = { tile: TileType.PropLow, glyph: '╤' };
const TABLE: PropSpec = { tile: TileType.PropLow, glyph: '╥' };
const CABINET: PropSpec = { tile: TileType.PropTall, glyph: '▤' };
const RACK: PropSpec = { tile: TileType.PropTall, glyph: '▥' };
const SHELF: PropSpec = { tile: TileType.PropTall, glyph: '≡' };
const FILES: PropSpec = { tile: TileType.PropTall, glyph: '§' };
const PLANT: PropSpec = { tile: TileType.PropDecor, glyph: '◊' };
/** The security desk / camera bank a guard mans. Waist-high, so the guard
 *  behind it still has cover and a clear view over the top. */
const STATION: PropSpec = { tile: TileType.PropLow, glyph: '╪' };

/** Cover/solid props each room type scatters along its perimeter. */
const PALETTE: Record<RoomType, PropSpec[]> = {
  stairwell: [], // kept clear — a transit room
  corridor: [],  // circulation: nothing solid, or you'd choke the only route
  lobby: [COUNTER],
  'open-office': [DESK, CABINET],
  'private-office': [DESK, CABINET],
  meeting: [TABLE],
  break: [COUNTER, RACK],
  restroom: [COUNTER],
  storage: [SHELF],
  utility: [RACK],
  security: [COUNTER, CABINET],
  records: [FILES],
  server: [RACK],
  // Plant rooms: machinery + shelving along the walls, so they read as cramped
  // service spaces rather than offices.
  boiler: [RACK],
  hvac: [RACK],
  pump: [RACK],
  fire: [CABINET],
  elevator: [RACK],
};

/** Tiles that must stay clear: every door + its 4-neighbours, plus the spawn. */
function keepClearSet(map: MapData): Set<number> {
  const clear = new Set<number>();
  const mark = (x: number, y: number) => {
    if (x >= 0 && y >= 0 && x < map.width && y < map.height) clear.add(y * map.width + x);
  };
  for (const d of map.doors) {
    mark(d.x, d.y);
    mark(d.x - 1, d.y);
    mark(d.x + 1, d.y);
    mark(d.x, d.y - 1);
    mark(d.x, d.y + 1);
  }
  mark(map.spawn.x, map.spawn.y);
  return clear;
}

/** Is (x,y) on the perimeter ring of floor rect `f`? */
function onRing(f: Rect, x: number, y: number): boolean {
  return x === f.x0 || x === f.x1 || y === f.y0 || y === f.y1;
}

function inFloor(f: Rect, x: number, y: number): boolean {
  return x >= f.x0 && x <= f.x1 && y >= f.y0 && y <= f.y1;
}

function walkable(t: TileType): boolean {
  return t === TileType.Floor || t === TileType.PropDecor;
}

/** A door-entry tile inside `f` (the interior tile orthogonally adjacent to a
 *  door on the room's wall) — always a clear seed for the connectivity flood. */
function roomEntry(map: MapData, f: Rect): { x: number; y: number } | null {
  for (const d of map.doors) {
    const cand = [
      { x: d.x - 1, y: d.y }, { x: d.x + 1, y: d.y },
      { x: d.x, y: d.y - 1 }, { x: d.x, y: d.y + 1 },
    ];
    for (const c of cand) if (inFloor(f, c.x, c.y)) return c;
  }
  return null;
}

/** Every walkable floor tile in `f` reachable from `seed` (props treated as walls)? */
function roomConnected(map: MapData, f: Rect, seed: { x: number; y: number }): boolean {
  const seen = new Set<number>();
  const stack = [seed.y * map.width + seed.x];
  while (stack.length) {
    const idx = stack.pop()!;
    if (seen.has(idx)) continue;
    seen.add(idx);
    const x = idx % map.width;
    const y = Math.floor(idx / map.width);
    if (!inFloor(f, x, y) || !walkable(tileAt(map, x, y))) continue;
    stack.push(idx - 1, idx + 1, idx - map.width, idx + map.width);
  }
  for (let y = f.y0; y <= f.y1; y++) {
    for (let x = f.x0; x <= f.x1; x++) {
      if (walkable(tileAt(map, x, y)) && !seen.has(y * map.width + x)) return false;
    }
  }
  return true;
}

/** Place a prop only if it keeps the room fully connected (else revert). */
function tryPlace(
  map: MapData, x: number, y: number, spec: PropSpec, clear: Set<number>,
  f: Rect, seed: { x: number; y: number },
): void {
  const idx = y * map.width + x;
  if (clear.has(idx) || tileAt(map, x, y) !== TileType.Floor) return;
  setTile(map, x, y, spec.tile); // writes tile + default glyph
  map.glyphs[idx] = spec.glyph;
  if (spec.tile !== TileType.PropDecor && !roomConnected(map, f, seed)) {
    setTile(map, x, y, TileType.Floor); // revert (restores '.' glyph)
  }
}

/**
 * Find a connectivity-safe interior-perimeter FLOOR tile in `cell` (off the door
 * lanes) where a solid prop could go without sealing the room — returns the tile
 * (left as Floor for the caller to fill), or null. Used to seat safes.
 */
export function findSolidSpot(map: MapData, cell: Rect, rng: () => number): { x: number; y: number } | null {
  const f = cellFloor(cell);
  const seed = roomEntry(map, f);
  if (!seed) return null;
  const clear = keepClearSet(map);
  const cands: { x: number; y: number }[] = [];
  for (let y = f.y0; y <= f.y1; y++) {
    for (let x = f.x0; x <= f.x1; x++) {
      if (onRing(f, x, y) && tileAt(map, x, y) === TileType.Floor && !clear.has(y * map.width + x)) {
        cands.push({ x, y });
      }
    }
  }
  for (let i = cands.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [cands[i], cands[j]] = [cands[j]!, cands[i]!];
  }
  for (const c of cands) {
    setTile(map, c.x, c.y, TileType.PropTall);
    const ok = roomConnected(map, f, seed);
    setTile(map, c.x, c.y, TileType.Floor); // leave it Floor for the caller
    if (ok) return c;
  }
  return null;
}

/**
 * Seat a manned SECURITY STATION in each security office: the desk goes on a
 * connectivity-safe spot and the tile a guard would stand at is registered in
 * `map.stations`. Rooms with nowhere safe to put one are simply skipped.
 */
export function placeStations(
  map: MapData,
  cells: Rect[],
  types: RoomType[],
  rng: () => number,
): void {
  cells.forEach((c, i) => {
    if (types[i] !== 'security') return;
    const spot = findSolidSpot(map, c, rng);
    if (!spot) return;
    setTile(map, spot.x, spot.y, STATION.tile);
    map.glyphs[spot.y * map.width + spot.x] = STATION.glyph;
    // The guard stands at the desk, not on it — first free neighbour.
    for (const [dx, dy] of [[0, 1], [0, -1], [1, 0], [-1, 0]] as const) {
      const sx = spot.x + dx;
      const sy = spot.y + dy;
      if (tileAt(map, sx, sy) === TileType.Floor) { map.stations.push({ x: sx, y: sy }); return; }
    }
  });
}

/**
 * Furnish every room. `roomLuxury` (0..1, from the build profile) scales density.
 * Placement is verified connectivity-safe per room — a prop that would seal off a
 * pocket is reverted — so the floor stays fully traversable.
 */
export function furnish(
  map: MapData,
  cells: Rect[],
  types: RoomType[],
  roomLuxury: number,
  rng: () => number,
): void {
  const clear = keepClearSet(map);
  const coverDensity = 0.32 + roomLuxury * 0.22;
  cells.forEach((c, i) => {
    const f = cellFloor(c);
    const seed = roomEntry(map, f);
    if (!seed) return; // no door into this room (shouldn't happen) — skip
    const palette = PALETTE[types[i]!];
    for (let y = f.y0; y <= f.y1; y++) {
      for (let x = f.x0; x <= f.x1; x++) {
        if (onRing(f, x, y)) {
          if (palette.length > 0 && rng() < coverDensity) {
            tryPlace(map, x, y, palette[Math.floor(rng() * palette.length)]!, clear, f, seed);
          }
        } else if (rng() < 0.05) {
          tryPlace(map, x, y, PLANT, clear, f, seed); // passable plant
        }
      }
    }
  });
}
